import React, { useMemo } from 'react';
import { diffLines as computeDiffLines } from 'diff';
import { MarkdownContent } from 'new/elements/MarkdownContent';
import styles from './MarkdownDiffView.module.scss';
import cx from 'classnames';

interface MarkdownDiffPanelProps {
  content: string;
  original: string;
  modified: string;
  highlight: 'removed' | 'added'; // Before=removed, After=added
  className?: string;
}

const normalizeText = (s: string): string => (s || '').replace(/\r\n|\r/g, '\n').trimEnd();

const getLines = (s: string): string[] => {
  const n = normalizeText(s);
  if (n === '') return [];
  return n.split('\n');
};

/** Group consecutive lines of the same type into blocks so lists/headings/code
 * fences render as rich text (not raw `#`/`-`/``` ``` ```). */
function groupLinesIntoBlocks(
  lines: string[],
  types: Array<'removed' | 'added' | 'unchanged'>,
): Array<{ block: string; type: 'removed' | 'added' | 'unchanged' }> {
  const blocks: Array<{ block: string; type: 'removed' | 'added' | 'unchanged' }> = [];
  const isListLine = (line: string) => /^\s*[-*]\s/.test(line) || /^\s*\d+\.\s/.test(line);
  const isFenceOpen = (line: string) => /^```/.test(line);
  const isFenceClose = (line: string) => /^```\s*$/.test(line);
  const isTableLine = (line: string) => /^\s*\|.*\|\s*$/.test(line);

  let i = 0;
  while (i < lines.length) {
    const type = types[i] ?? 'unchanged';
    const line = lines[i];

    if (isTableLine(line)) {
      // Collect the contiguous run of table rows (incl. the `| --- |` divider).
      const start = i;
      const chunk: string[] = [];
      while (i < lines.length && isTableLine(lines[i])) {
        chunk.push(lines[i]);
        i++;
      }
      const uniform = chunk.every((_, k) => types[start + k] === type);
      // Render as a real table ONLY when the whole table shares one diff state, so
      // the block-level marker stays accurate. If rows changed differently, fall
      // back to raw per-line rows so no per-row diff marker is lost.
      if (uniform && chunk.length > 1) {
        blocks.push({ block: chunk.join('\n'), type });
      } else {
        for (let k = 0; k < chunk.length; k++) {
          blocks.push({ block: chunk[k], type: types[start + k] ?? 'unchanged' });
        }
      }
    } else if (isFenceOpen(line)) {
      const chunk: string[] = [line];
      i++;
      while (i < lines.length) {
        chunk.push(lines[i]);
        const done = isFenceClose(lines[i]);
        i++;
        if (done) break;
      }
      blocks.push({ block: chunk.join('\n'), type });
    } else if (isListLine(line)) {
      const chunk: string[] = [];
      while (
        i < lines.length &&
        types[i] === type &&
        (isListLine(lines[i]) || /^\s+\S/.test(lines[i]))
      ) {
        chunk.push(lines[i]);
        i++;
      }
      blocks.push({ block: chunk.join('\n'), type });
    } else {
      blocks.push({ block: line, type });
      i++;
    }
  }
  return blocks;
}

/**
 * Renders markdown content as rich text with line-level diff highlighting.
 * Before = removed (red), After = added (green). Lines are grouped into blocks
 * so multi-line markdown constructs render correctly.
 */
export const MarkdownDiffPanel: React.FC<MarkdownDiffPanelProps> = ({
  content,
  original,
  modified,
  highlight,
  className,
}) => {
  const blocksWithTypes = useMemo(() => {
    const normOrig = normalizeText(original || '');
    const normMod = normalizeText(modified || '');

    const changes = computeDiffLines(normOrig, normMod);
    const origTypes: Array<'removed' | 'unchanged'> = [];
    const modTypes: Array<'added' | 'unchanged'> = [];

    for (const change of changes) {
      const raw = (change.value || '').replace(/\r\n|\r/g, '\n');
      const lineContents = raw.split('\n');
      if (lineContents.length > 1 && lineContents[lineContents.length - 1] === '') {
        lineContents.pop();
      }
      for (let i = 0; i < lineContents.length; i++) {
        if (change.removed) {
          origTypes.push('removed');
        } else if (change.added) {
          modTypes.push('added');
        } else {
          origTypes.push('unchanged');
          modTypes.push('unchanged');
        }
      }
    }

    const contentLines = getLines(content || '');
    const types = highlight === 'removed' ? origTypes : modTypes;
    const lineTypes = contentLines.map((_, i): 'removed' | 'added' | 'unchanged' =>
      i < types.length ? (types[i] as 'removed' | 'added' | 'unchanged') : 'unchanged',
    );
    return groupLinesIntoBlocks(contentLines, lineTypes);
  }, [content, original, modified, highlight]);

  return (
    <div className={cx(styles.panel, className)}>
      {blocksWithTypes.map(({ block, type }, index) => (
        <div
          key={`block-${index}`}
          className={cx(styles.panelLine, {
            [styles.lineAdded]: type === 'added',
            [styles.lineRemoved]: type === 'removed',
          })}
        >
          <MarkdownContent>{block || '\n'}</MarkdownContent>
        </div>
      ))}
    </div>
  );
};
